import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';

import { bemCssModules, getTestId, isObject } from '@utils';
import { useIsMobile, useModal } from '@utils/hooks';

import Dropdown, { TDropdown, BaseDataObjectType } from '@components/Dropdown';
import InputOld from '@components/InputOld';

import Header from './Header';

import style from './SearchInput.scss';

const cn = bemCssModules(style, 'SearchInput');
const ti = getTestId('SearchInput');

export type TSearchInput<DataObjectType> = {
  className?: string;
  label: string;
  placeholder: string;
  customOption?: TDropdown<DataObjectType>['customOption'];
  data: TDropdown<DataObjectType>['data'];
  onChange?: TDropdown<DataObjectType>['onChange'];
  dropdownClassName?: string;
  onInput?: (value: string) => void;
  outerValue?: string;
  onlyDataValueAvailable?: boolean;
  error?: string;
};

const SearchInput = <DataObjectType extends BaseDataObjectType>(
  props: PropsWithChildren<TSearchInput<DataObjectType>>
) => {
  const {
    className,
    label,
    placeholder,
    data,
    customOption,
    onChange,
    dropdownClassName,
    onInput,
    outerValue,
    onlyDataValueAvailable,
    error,
  } = props;

  const ref = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen, dropdownRef] = useModal([ref]);
  const [search, setSearch] = useState('');
  const isMobile = useIsMobile();

  useEffect(() => {
    if (outerValue) setSearch(outerValue);
  }, [outerValue]);

  useEffect(() => {
    if (!isOpen && outerValue) setSearch(outerValue);
  }, [isOpen, outerValue]);

  const valueForSearch = search.toLowerCase();
  const filterMethod = (element: DataObjectType) => {
    switch (typeof element) {
      case 'string':
        return element.toLowerCase().includes(valueForSearch);
      case 'object':
        if (element === null) return null;
        return Object.values(element).some(el =>
          el?.toString().toLowerCase().includes(valueForSearch)
        );
      default:
        return null;
    }
  };

  const dataAfterFilter: () => TSearchInput<DataObjectType>['data'] = () => {
    if (Array.isArray(data)) {
      return data.filter(element => filterMethod(element));
    }
    if (isObject(data)) {
      return Object.keys(data).reduce((acc, el) => {
        acc[el] = data[el].filter(element => filterMethod(element));
        if (acc[el].length <= 0) delete acc[el];
        return acc;
      }, {});
    }
    return undefined;
  };
  const filteredData = dataAfterFilter();

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearch(value);
    onInput?.(value);
    if (!isOpen) setIsOpen(true);
  };

  const onOpen = () => {
    const isOpenDropDown = filteredData && Object.values(filteredData).flat().length > 0;
    if (isOpenDropDown || isMobile) {
      if (isMobile) window.scrollTo({ top: window.innerHeight });
      setIsOpen(true);
    }
  };

  const onClose = () => setIsOpen(false);

  const onInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.preventDefault();
    onOpen();
  };

  const headerProps = { label, search, onSearch, placeholder, onEnter: onClose };

  const desktopHidden =
    filteredData &&
    ((Array.isArray(filteredData) && filteredData.length <= 0) ||
      Object.keys(filteredData).length <= 0);

  return (
    <div className={cn(null, [className])} data-testid={ti()} ref={ref}>
      <InputOld
        label={label}
        placeholder={placeholder}
        value={isOpen ? search : onlyDataValueAvailable ? outerValue : outerValue || search}
        onChange={onSearch}
        onClick={onOpen}
        onTouchEnd={onOpen}
        onFocus={onInputFocus}
        autoComplete="off"
        error={error}
      />
      <button className={cn('overlay-button')} type="button" onClick={onOpen}>
        open
      </button>
      <Dropdown
        ref={dropdownRef}
        className={cn('dropdown', { desktopHidden }, [dropdownClassName])}
        isOpen={isOpen}
        header={<Header {...headerProps} />}
        data={filteredData}
        controller={ref}
        customOption={customOption}
        classNames={{ defaultOption: cn('default-option') }}
        onChange={el => {
          const value = typeof el === 'string' ? el : el.label || '';
          setSearch(value);
          onInput?.(value);
          onChange?.(el);
          setIsOpen(false);
        }}
        onClose={onClose}
      />
    </div>
  );
};

export default SearchInput;
